import React, { useEffect } from 'react';
import { Route, useHistory } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import ReactGA from 'react-ga';


/**
 * React hook for scrolling to the top of the page when the history has changed.
 */
function useScrollTopOnHistory() {
  const history = useHistory();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [history.location.pathname]);
}


/**
 * A custom hook that does neccessary actions on any route change.
 * This includes:
 *  * Scroll the window to the page top.
 *  * Changing the document title
 *  * Notifying Google analytics
 * @param {string} titleKey A react-intl key.
 */
function useOnRouteChange(titleKey) {
  useScrollTopOnHistory();

  const intl = useIntl();
  useEffect(() => {
    // Update the title of the window
    const pageTitle = titleKey ? `${intl.formatMessage({ id: titleKey })}` : undefined;
    const appTitle = `${intl.formatMessage({ id: 'platformTitle' })}`;
    document.title = `${pageTitle || ''}${pageTitle ? ' | ' : ''}${appTitle}`; // [pagetitle | apptitle] OR [apptitle]

    // Update google analytics
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, [intl, titleKey]);
}


/**
 * A basic route in the VIA application.
 * This is used for sub-routes within the application.
 * If you need an entry point for the application, use the ViaEntryRoute component instead.
 *
 * @param {object} props The react props
 * @param {string} props.titleKey The translation key used for the window title.
 */
export default function ViaRoute({ titleKey, ...rest }) {
  useOnRouteChange(titleKey);

  return <Route {...rest} />;
}

ViaRoute.propTypes = {
  titleKey: PropTypes.string.isRequired,
};
